import { v4 as uuid } from "uuid";
import { OmsProduct } from "../lusca-search-api/generated-src";
import type { OmsFilterDraft } from "./useOmsFilterCriteria";
import { isStoredOmsReport } from "./omsReportMetadata";

const storageKey = (product: string) => `oms-export-draft:${product}`;
// Remounting within this page preserves work; a full page load gets a new ID.
const pageSessionId = uuid();
const maxDraftBytes = 256 * 1024;
type Check = (value: unknown) => boolean;

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const string: Check = (value) => typeof value === "string";
const boolean: Check = (value) => typeof value === "boolean";
const count: Check = (value) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;
const timestamp: Check = (value) =>
  typeof value === "string" && Number.isFinite(Date.parse(value));
const strings: Check = (value) => Array.isArray(value) && value.every(string);
const oneOf =
  (...values: unknown[]): Check =>
  (value) =>
    values.includes(value);

function optional(
  value: Record<string, unknown>,
  key: string,
  check: Check
): boolean {
  return value[key] === undefined || check(value[key]);
}

function filters(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.length <= 20 &&
    value.every(
      (item) =>
        record(item) &&
        string(item.column) &&
        string(item.operator) &&
        optional(item, "value", string) &&
        optional(item, "values", strings) &&
        optional(item, "valueNotice", string)
    )
  );
}

function criteria(value: unknown): boolean {
  if (!filters(value)) return false;
  const items = value as Record<string, unknown>[];
  return (
    items.every(
      (item) => Number.isSafeInteger(item.id) && (item.id as number) >= 0
    ) && new Set(items.map((item) => item.id)).size === items.length
  );
}

function columns(value: unknown): boolean {
  return (
    record(value) &&
    oneOf("all", "selected")(value.strategy) &&
    strings(value.names) &&
    optional(value, "touched", boolean) &&
    optional(value, "customOrder", boolean)
  );
}

function columnTypes(value: unknown): boolean {
  return (
    record(value) &&
    Object.values(value).every(oneOf("string", "number", "date", "datetime"))
  );
}

function preset(value: unknown): boolean {
  return (
    record(value) &&
    string(value.presetId) &&
    string(value.name) &&
    oneOf("filters", "columns", "both")(value.scope) &&
    Number.isInteger(value.revision) &&
    (value.revision as number) > 0 &&
    value.formatVersion === 1 &&
    timestamp(value.createdAt) &&
    timestamp(value.updatedAt) &&
    string(value.schemaFingerprint) &&
    columnTypes(value.schemaTypes) &&
    optional(value, "rows", oneOf("all", "filtered")) &&
    optional(value, "operation", oneOf("and", "or")) &&
    optional(value, "filters", filters) &&
    optional(
      value,
      "columns",
      (item) =>
        record(item) &&
        oneOf("all", "selected")(item.strategy) &&
        optional(item, "names", strings) &&
        (item.strategy === "all" || strings(item.names))
    )
  );
}

function settings(value: unknown): boolean {
  return (
    record(value) &&
    oneOf("full", "filtered")(value.mode) &&
    criteria(value.criteria) &&
    oneOf("and", "or")(value.operation) &&
    columns(value.columns)
  );
}

function configuration(value: unknown): boolean {
  return (
    record(value) &&
    settings(value) &&
    optional(value, "presetBaseline", settings) &&
    optional(value, "appliedPreset", preset)
  );
}

function identity(value: unknown): boolean {
  return record(value) && string(value.identity) && string(value.id);
}

function requests(value: unknown): boolean {
  return (
    record(value) &&
    optional(value, "full", identity) &&
    optional(value, "filtered", identity)
  );
}

function editor(value: unknown, product: OmsProduct): boolean {
  return (
    record(value) &&
    oneOf("export", "edit", "save", "manage")(value.view) &&
    string(value.name) &&
    oneOf("filters", "columns", "both")(value.scope) &&
    optional(value, "updating", boolean) &&
    optional(value, "returnTo", oneOf("save", "actions", "edit")) &&
    optional(value, "cancelTo", oneOf("export", "manage")) &&
    optional(value, "previous", configuration) &&
    optional(value, "previousReport", (item) =>
      isStoredOmsReport(item, product)
    ) &&
    optional(value, "previousWorkspace", oneOf("rows", "columns")) &&
    optional(value, "previousRequests", requests) &&
    optional(
      value,
      "saveCheckpoint",
      (item) =>
        record(item) &&
        configuration(item.configuration) &&
        isStoredOmsReport(item.report, product) &&
        oneOf("rows", "columns")(item.workspace) &&
        requests(item.requests)
    )
  );
}

export function readOmsDraft(
  product: OmsProduct,
  namespace = product as string
): OmsFilterDraft | undefined {
  try {
    const text = sessionStorage.getItem(storageKey(namespace));
    if (!text || new Blob([text]).size > maxDraftBytes) return undefined;
    const saved: unknown = JSON.parse(text);
    if (!record(saved) || saved.version !== 1 || !record(saved.draft))
      return undefined;
    const { draft } = saved;
    if (
      !isStoredOmsReport(draft.report, product) ||
      draft.snapshotId !== draft.report.snapshotId ||
      !oneOf("full", "filtered")(draft.tab) ||
      !oneOf("and", "or")(draft.operation) ||
      !boolean(draft.editing) ||
      !optional(draft, "pendingExport", boolean) ||
      !optional(draft, "pendingPreset", boolean) ||
      !criteria(draft.criteria) ||
      !columns(draft.columns) ||
      !optional(draft, "workspace", oneOf("rows", "columns")) ||
      !optional(draft, "columnsExpanded", boolean) ||
      !optional(draft, "appliedPreset", preset) ||
      !optional(draft, "presetBaseline", settings) ||
      !optional(draft, "undo", configuration) ||
      !optional(draft, "undoRequests", requests) ||
      !optional(
        draft,
        "viewingPrevious",
        (value) => record(value) && oneOf("full", "filtered")(value.mode)
      ) ||
      !optional(draft, "presetEditor", (value) => editor(value, product)) ||
      !optional(draft, "requests", requests) ||
      !optional(
        draft,
        "request",
        (value) =>
          identity(value) &&
          oneOf("full", "filtered")((value as Record<string, unknown>).mode)
      ) ||
      !optional(
        draft,
        "presetRequest",
        (value) =>
          identity(value) &&
          optional(value as Record<string, unknown>, "attemptedAt", count)
      )
    )
      return undefined;
    return draft as OmsFilterDraft;
  } catch {
    return undefined;
  }
}

export function clearOmsDraft(
  product: OmsProduct,
  namespace = product as string
) {
  try {
    sessionStorage.removeItem(storageKey(namespace));
  } catch {
    // In-memory editing remains available when browser storage is disabled.
  }
}

export function writeOmsDraft(
  product: OmsProduct,
  draft: OmsFilterDraft,
  namespace = product as string
) {
  try {
    const text = JSON.stringify({ version: 1, pageSessionId, draft });
    if (new Blob([text]).size > maxDraftBytes) {
      clearOmsDraft(product, namespace);
      return;
    }
    sessionStorage.setItem(storageKey(namespace), text);
  } catch {
    clearOmsDraft(product, namespace);
    // In-memory editing and accepted-job recovery still work without draft storage.
  }
}

export function hasPendingOmsRequest(draft?: OmsFilterDraft): boolean {
  if (!draft) return false;
  // Older drafts did not distinguish an uncertain POST from an ordinary edit.
  const pendingExport =
    draft.pendingExport ??
    Boolean(
      draft.editing &&
        (draft.request || draft.requests?.full || draft.requests?.filtered)
    );
  return pendingExport || (draft.pendingPreset ?? Boolean(draft.presetRequest));
}

export function pendingOmsMutation(mutation: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}): boolean {
  if (mutation.isLoading) return true;
  if (!mutation.isError) return false;
  const status = (mutation.error as { response?: { status?: number } })
    ?.response?.status;
  // A definite rejection can be discarded; network/server failures may hide acceptance.
  return ![400, 401, 403, 404, 409, 412, 413, 422].includes(status || 0);
}

export function readOmsRecoveryDraft(
  product: OmsProduct,
  namespace = product as string
) {
  const draft = readOmsDraft(product, namespace);
  if (!draft) {
    clearOmsDraft(product, namespace);
    return undefined;
  }
  let samePage = false;
  try {
    const saved: unknown = JSON.parse(
      sessionStorage.getItem(storageKey(namespace)) || "null"
    );
    samePage = record(saved) && saved.pageSessionId === pageSessionId;
  } catch {
    // Uncertain requests still recover when page identity cannot be established.
  }
  if (draft.pendingExport) {
    const attempts = (["full", "filtered"] as const).flatMap((mode) => {
      const request =
        draft.requests?.[mode] ||
        (draft.request?.mode === mode ? draft.request : undefined);
      return request ? [{ mode, id: request.id }] : [];
    });
    try {
      const accepted =
        attempts.length > 0 &&
        attempts.every(({ mode, id }) => {
          const saved: unknown = JSON.parse(
            sessionStorage.getItem(`oms-export-latest:${namespace}:${mode}`) ||
              "null"
          );
          return (
            record(saved) &&
            saved.requestId === id &&
            string(saved.jobId) &&
            isStoredOmsReport(saved.report, product)
          );
        });
      // A POST can complete after the modal closes; its accepted job is recovered separately.
      if (accepted) {
        draft.pendingExport = false;
        writeOmsDraft(product, draft, namespace);
      }
    } catch {
      // Retain the original request when acceptance cannot be established.
    }
  }
  if (samePage || hasPendingOmsRequest(draft)) return draft;
  clearOmsDraft(product, namespace);
  return undefined;
}
